import { useEffect, useState } from "react";
import {
    fetchClientsApi,
    deleteClientApi,
    createClientApi,
    updateClientApi,
} from "../../services/api/boq/clientService";
import toast, { Toaster } from "react-hot-toast";
import { FiEye, FiEdit, FiTrash2 } from "react-icons/fi";
import Modal from "react-modal";

Modal.setAppElement("#root");

export default function ClientList() {
    const [clients, setClients] = useState([]);
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("name");
    const [sortOrder, setSortOrder] = useState("asc");
    const [loading, setLoading] = useState(false);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        contact_person:"",
        gst_no: "",
        pancard_no: "",
        address: "",
        in_site_location:"",
        status: "active",
    });

    /* LOAD CLIENTS */
    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setLoading(true);
            const data = await fetchClientsApi();
            console.log("Fetched clients:", data);
            setClients(Array.isArray(data) ? data : data?.data || []);
        } catch {
            toast.error("Failed to load clients");
        } finally {
            setLoading(false);
        }
    };

    /* SEARCH */
    const filteredClients = clients.filter((c) =>
        `${c.client_code} ${c.name} ${c.email} ${c.company_name || ""}`
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    /* SORT */
    const sortedClients = [...filteredClients].sort((a, b) => {
        const A = (a[sortKey] || "").toString().toLowerCase();
        const B = (b[sortKey] || "").toString().toLowerCase();
        return sortOrder === "asc"
            ? A.localeCompare(B)
            : B.localeCompare(A);
    });

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortOrder("asc");
        }
    };

    /* DELETE */
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this client?")) return;
        try {
            await deleteClientApi(id);
            toast.success("Client deleted");
            loadClients();
        } catch {
            toast.error("Delete failed");
        }
    };

    /* VIEW */
    const handleView = (client) => {
        setSelectedClient(client);
        setViewModalOpen(true);
    };

    /* CREATE */
    const handleAdd = () => {
        setSelectedClient(null);
        setForm({
            name: "",
            email: "",
            phone: "",
            company_name: "",
            contact_person:"",
            gst_no: "",
            pancard_no: "",
            address: "",
            in_site_location:"",
            status: "active",
        });
        setFormModalOpen(true);
    };

    /* EDIT */
    const handleEdit = (client) => {
        setSelectedClient(client);
        setForm({
            name: client.name,
            email: client.email,
            phone: client.phone,
            company_name: client.company_name || "",
            contact_person:client.contact_person || "",
            gst_no: client.gst_no || "",
            pancard_no: client.pancard_no || "",
            address: client.address || "",
            in_site_location: client.in_site_location || "",
            status: client.status,
        });
        setFormModalOpen(true);
    };

    /* FORM */
    const handleChange = (e) => {
        const { name, value } = e.target;

        let updatedValue = value;

        // Auto uppercase for GST & PAN
        if (name === "gst_no" || name === "pancard_no") {
            updatedValue = value.toUpperCase();
        }

        setForm(prev => ({
            ...prev,
            [name]: updatedValue
        }));

        validateField(name, updatedValue);
    };

    const validateField = (name, value) => {
        let error = "";

        if (name === "name" && !value.trim()) {
            error = "Name is required";
        }

        if (name === "email") {
            if (!/\S+@\S+\.\S+/.test(value))
                error = "Enter valid email address";
        }

        if (name === "phone") {
            if (!/^[0-9]*$/.test(value))
                error = "Only numbers allowed";
            else if (value.length > 10)
                error = "Phone number must be 10 digits";
            else if (value.length < 10)
                error = "Phone number must be 10 digits";
        }

        if (name === "pancard_no") {
            const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
            if (value.length > 10)
                error = "PAN must be 10 characters";
            else if (value && !panRegex.test(value))
                error = "Enter valid PAN (ABCDE1234F)";
        }

        if (name === "gst_no") {
            const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
            if (value.length > 15)
                error = "GST must be 15 characters";
            else if (value && !gstRegex.test(value))
                error = "Enter valid GST (22ABCDE1234F1Z5)";
        }

        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const validateForm = () => {

        const requiredFields = ["name", "email", "phone"];

        for (let field of requiredFields) {
            if (!form[field]) {
                toast.error(`${field.replace("_", " ")} is required`);
                return false;
            }
        }

        if (Object.values(errors).some(err => err)) {
            toast.error("Please fix validation errors");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (selectedClient) {
                await updateClientApi(selectedClient.id, form);
                toast.success("Client updated");
            } else {
                await createClientApi(form);
                toast.success("Client created");
            }
            setFormModalOpen(false);
            loadClients();
        } catch {
            toast.error("Save failed");
        }
    };

    return (
        <div className="p-6">
            <Toaster position="top-right" />

            {/* HEADER */}
            <div className="flex justify-between mb-4">
                <h2 className="text-2xl font-semibold">Clients</h2>
                <button
                    onClick={handleAdd}
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                    + Add Client
                </button>
            </div>

            {/* SEARCH */}
            <input
                className="border p-2 mb-4 w-full md:w-72 rounded"
                placeholder="Search client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            {/* TABLE */}
            {/* TABLE for large screens */}
            <div className="hidden md:block bg-white rounded shadow overflow-x-auto">
                <table className="min-w-full text-sm text-center">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="p-2 sm:p-3">Sl No</th>
                            <th className="p-2 sm:p-3">Code</th>
                            <th
                                className="p-2 sm:p-3 cursor-pointer"
                                onClick={() => handleSort("name")}
                            >
                                Name
                            </th>
                            <th className="p-2 sm:p-3">Email</th>
                            <th className="p-2 sm:p-3">Phone</th>
                            <th className="p-2 sm:p-3">PAN</th>
                            <th className="p-2 sm:p-3">GST</th>
                            <th className="p-2 sm:p-3">Address</th>
                            <th className="p-2 sm:p-3">Company</th>
                            <th className="p-2 sm:p-3">Status</th>
                            <th className="p-2 sm:p-3 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan="8" className="p-6 text-center">
                                    Loading...
                                </td>
                            </tr>
                        )}

                        {!loading && sortedClients.length === 0 && (
                            <tr>
                                <td colSpan="8" className="p-6 text-center">
                                    No clients found
                                </td>
                            </tr>
                        )}

                        {sortedClients.map((c, index) => (
                            <tr key={c.id} className="border-t hover:bg-blue-50 font-normal text-sm">
                                <td className="p-2 sm:p-3 font-medium">{index + 1}</td>
                                <td className="p-2 sm:p-3">{c.client_code}</td>
                                <td className="p-2 sm:p-3">{c.name}</td>
                                <td className="p-2 sm:p-3">{c.email}</td>
                                <td className="p-2 sm:p-3">{c.phone}</td>
                                <td className="p-2 sm:p-3">{c.pancard_no || "-"}</td>
                                <td className="p-2 sm:p-3">{c.gst_no || "-"}</td>
                                <td className="p-2 sm:p-3">{c.address || "-"}</td>
                                <td className="p-2 sm:p-3">{c.company_name || "-"}</td>
                                
                                <td className="p-2 sm:p-3">
                                    <span
                                        className={`px-2 py-1 rounded text-xs sm:text-sm ${c.status === "active"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {c.status}
                                    </span>
                                </td>
                                <td className="p-2 sm:p-3 text-center flex justify-center gap-2">
                                    <button
                                        onClick={() => handleView(c)}
                                        className="text-blue-600 text-lg sm:text-base"
                                    >
                                        <FiEye />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(c)}
                                        className="text-green-600 text-lg sm:text-base"
                                    >
                                        <FiEdit />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(c.id)}
                                        className="text-red-600 text-lg sm:text-base"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* CARD layout for mobile */}
            <div className="md:hidden space-y-4">
                {loading && <p className="text-center p-4">Loading...</p>}
                {!loading && sortedClients.length === 0 && (
                    <p className="text-center p-4">No clients found</p>
                )}
                {sortedClients.map((c, index) => (
                    <div
                        key={c.id}
                        className="bg-white p-4 rounded shadow hover:bg-blue-50"
                    >
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-semibold">{c.name}</h3>
                            <div className="flex gap-2 text-lg">
                                <button
                                    onClick={() => handleView(c)}
                                    className="text-blue-600"
                                    title="View"
                                >
                                    <FiEye />
                                </button>
                                <button
                                    onClick={() => handleEdit(c)}
                                    className="text-green-600"
                                    title="Edit"
                                >
                                    <FiEdit />
                                </button>
                                <button
                                    onClick={() => handleDelete(c.id)}
                                    className="text-red-600"
                                    title="Delete"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>
                        <div className="text-sm text-gray-700 space-y-1">
                            <p>
                                <span className="font-light ">Code:</span> {c.client_code}
                            </p>
                            <p>
                                <span className="font-normal">Email:</span> {c.email}
                            </p>
                            <p>
                                <span className="font-medium">Phone:</span> {c.phone}
                            </p>
                            <p>
                                <span className="font-medium">GST:</span> {c.gst_no || "-"}
                            </p>
                            <p>
                                <span className="font-medium">PAN:</span> {c.pancard_no || "-"}
                            </p>
                            <p>
                                <span className="font-normal">Address:</span> {c.address || "-"}
                            </p>
                            <p>
                                <span className="font-normal">Alternate Phone:</span> {c.alternate_phone || "-"}
                            </p>
                            <p>
                                <span className="font-normal">Status:</span>{" "}
                                <span
                                    className={`px-2 py-1 rounded text-xs ${c.status === "active"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                        }`}
                                >
                                    {c.status}
                                </span>
                            </p>
                        </div>
                    </div>
                ))}
            </div>



            {/* VIEW MODAL */}
            <Modal
                isOpen={viewModalOpen}
                onRequestClose={() => setViewModalOpen(false)}
                className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-auto mt-20 outline-none outline-none  h-auto max-h-[90vh]  overflow-y-auto"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start px-4 z-50  overflow-y-auto"
            >
                {selectedClient && (
                    <>
                        {/* Header */}
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                Client Details
                            </h2>
                            <button
                                onClick={() => setViewModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 text-xl"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <Detail label="Client Code" value={selectedClient.client_code} />

                            <Detail label="Status">
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium
              ${selectedClient.status === "active"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                        }
            `}
                                >
                                    {selectedClient.status}
                                </span>
                            </Detail>

                            <Detail label="Name" value={selectedClient.name} />
                            <Detail label="Phone" value={selectedClient.phone} />
                            <Detail label="Alternate Phone" value={selectedClient.alternate_phone || "-"} />
                            <Detail label="Email" value={selectedClient.email} />
                            <Detail label="GST No" value={selectedClient.gst_no || "-"} />
                            <Detail label="PAN No" value={selectedClient.pancard_no || "-"} />

                            <Detail
                                label="Company"
                                value={selectedClient.company_name || "-"}
                            />

                            <Detail label="Contact Person" value={selectedClient.contact_person || "-"} />

                            <Detail
                                label="Address"
                                value={selectedClient.address || "-"}
                                full
                            />
                            <Detail
                                label="Site Location"
                                value={selectedClient.in_site_location || "-"}
                                full
                            />
                        </div>

                        {/* Footer */}
                        <div className="border-t px-6 py-4 flex justify-end">
                            <button
                                onClick={() => setViewModalOpen(false)}
                                className="bg-gray-800 text-white px-5 py-2 rounded-lg hover:bg-gray-900"
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            {/* CREATE / EDIT MODAL */}
            <Modal isOpen={formModalOpen} onRequestClose={() => setFormModalOpen(false)}
                className="bg-white p-6 rounded max-w-xl mx-auto mt-24"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50">
                <h2 className="text-xl mb-4">
                    {selectedClient ? "Edit Client" : "Add Client"}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-3 grid grid-cols-2 gap-4">
                    <div>
                        <input name="name" value={form.name} onChange={handleChange} placeholder="Name" className="border p-2 w-full rounded mt-3" />
                    </div>
                    <div>
                        <input name="email" value={form.email} onChange={handleChange} placeholder="Email" className="border p-2 w-full rounded " />
                    </div>
                    <div>
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className="border p-2 w-full rounded" maxLength={10} />
                        {errors.phone && (
                            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                        )}
                    </div>
                    <div>
                        <input name="alternate_phone" value={form.alternate_phone} onChange={handleChange} placeholder="Alternate Phone" className="border p-2 w-full rounded" maxLength={15} />
                        {errors.alternate_phone && (
                            <p className="text-red-500 text-xs mt-1">{errors.alternate_phone}</p>
                        )}
                    </div>
                    <div>
                        <input name="company_name" value={form.company_name} onChange={handleChange} placeholder="Company Name" className="border p-2 w-full rounded" />
                    </div>
                    <div>
                        <input name="contact_person" value={form.contact_person} onChange={handleChange} placeholder="Contact Person" className="border p-2 w-full rounded" />
                    </div>
                    <div>
                        <input name="gst_no" value={form.gst_no} onChange={handleChange} placeholder="GST No" className="border p-2 w-full rounded" maxLength={15} />
                        {errors.gst_no && (
                            <p className="text-red-500 text-xs mt-1">{errors.gst_no}</p>
                        )}
                    </div>
                    <div>
                        <input name="pancard_no" value={form.pancard_no} onChange={handleChange} placeholder="PAN No" className="border p-2 w-full rounded" maxLength={10} />
                        {errors.pancard_no && (
                            <p className="text-red-500 text-xs mt-1">{errors.pancard_no}</p>
                        )}
                    </div>
                    <div>
                        <textarea name="address" value={form.address} onChange={handleChange} placeholder="Address" className="border p-2 w-full rounded" />
                    </div>

                    <div>
                        <textarea name="in_site_location" value={form.in_site_location} onChange={handleChange} placeholder="In-Site Location" className="border p-2 w-full rounded" />
                    </div>

                    <div className="flex gap-2">
                        <button type="button" onClick={() => setFormModalOpen(false)} className="bg-gray-300 px-4 py-2 rounded">
                            Cancel
                        </button>
                        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
                            Save
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
function Detail({ label, value, children, full }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <p className="text-gray-500 mb-1">{label}</p>
            <p className="font-medium text-gray-800 break-words">
                {children || value}
            </p>
        </div>
    );
}